import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormikValues } from 'formik';
import { setGroup } from 'Store';
import useUpdateRule from 'Hooks/query-hooks/global-rules/useUpdateRule';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import AddRuleForm from 'Components/AddRulePage';
import { getValueByParamType } from 'Components/AddRulePage/RuleStatement/Condition/helper';
import Layout from 'Components/Layout';

type UpdateSimpleRuleLayoutProps = {
    parameterData: UseGetParametersResponse | undefined;
    currentRule: CustomGetRuleAPIResponse;
    lData: ListApiGetResponse;
    overrideData: RevisionMerchantOverrides | RevisionTenantGroupOverrides;
    selectedRowId?: string;
    repoId: number;
    conclusionFlagListData: ConclusionListApiGetResponse[];
};

const UpdateSimpleRuleLayout = ({
    parameterData,
    currentRule,
    lData,
    overrideData,
    selectedRowId,
    repoId,
    conclusionFlagListData
}: UpdateSimpleRuleLayoutProps) => {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const { addModal } = useReduxModal();
    const history = useHistory();
    const formRef = React.useRef<FormikValues>();
    const dispatch = useDispatch();
    const { data: landlordInfo } = useGetLandlordInfo(true);

    // transform data to map it to AddRuleForm
    const formInitialValues: NewRuleFormSchema = {
        id: currentRule.id, // rule uid
        ruleId: currentRule.ruleId, // rule name
        group: [String(currentRule.group)],
        priority: currentRule.priority || 1,
        active: currentRule.active,
        description: currentRule.description,
        actions: currentRule.actions,
        comment: currentRule.comment ?? '',
        conditions: currentRule.conditions.map((condition) => {
            const paramObj = parameterData?.fieldNameHashmap[condition.fieldName];
            // when the parameter API doesn't contain the system name (fieldName) we're looking for,
            // set the value type of the current condition to string by default to make UI work
            const paramType = paramObj?.type ?? 'string';

            return {
                conditionId: condition.conditionId,
                parameterField: {
                    name: [condition.fieldName],
                    displayName: condition.message,
                    type: paramType
                },
                operatorField: [condition.operator],
                valueField: getValueByParamType(condition.values, condition.operator, paramType),
                // FE generates its own valueType when users interact with input fields
                // It's because there's a FreeEntry component that can be both string and number types
                // this puts constraints on Yup running unique validation schemas for valueField based on ValueType
                // so don't initialize!
                // ref: AddRulePage/schema.ts
                valueType: '',
                readOnly: condition.readOnly
            };
        }),
        createdBy: currentRule?._formatted?.createdBy,
        createdAt: currentRule?._formatted?.createdAt,
        // instead of prop drilling, we can access/pass additional values through formik
        _custom: {
            overrideExist: overrideData?.[currentRule.ruleId]?.length > 0 ?? false
        },
        allowOverrides: currentRule?.allowOverrides || false,
        hiddenFromTenant: currentRule?.hiddenFromTenant || false
    };

    const onSuccess = (_data: any, { ruleId }: any) => {
        dispatch(setGroup(''));
        addAlert({
            id: 'update-rule',
            content: t('updateRuleSuccess', { ruleId }),
            status: 'success'
        });
        history.push(`/global-rules?selectedRowId=${selectedRowId}`);
    };

    const onError = (err: any, { ruleId }: any) => {
        const handleDefault = () => {
            addModal({
                id: 'update-rule-error',
                status: 'error',
                title: t('Unable to update rule'),
                content: (
                    <>
                        {t('updateRuleErrorSpecific', {
                            ruleId,
                            errMsg:
                                err?.data?.error?.message ??
                                err?.data?.error?.msg?.error ??
                                err?.response?.data?.error?.message
                        })}
                    </>
                ),
                cancelText: t('Close')
            });
        };

        const handleNotFound = () => {
            if (err?.response?.data?.message === t('ruleNotFound')) {
                addModal({
                    id: 'error-saving-rule',
                    status: 'warning',
                    title: t('Error Saving Rule'),
                    content: t('Rule Could Not Be Saved'),
                    showCloseIcon: false,
                    showCloseButton: false,
                    closeOnAction: true,
                    onActionText: 'Confirm',
                    onClose: () => {
                        history.replace('/global-rules');
                    }
                });
            } else {
                handleDefault();
            }
        };

        switch (err?.response?.status) {
            case 404:
                handleNotFound();
                break;
            default:
                handleDefault();
                break;
        }

        if (formRef.current) {
            formRef.current.setSubmitting(false);
        }
    };

    const mutation = useUpdateRule(formInitialValues.id!, {
        templateRepositoryId: repoId,
        mutationOptions: {
            onSuccess,
            onError
        }
    });

    const disableDefinitionChange = currentRule.canEditDefinition === false;
    const disableOverrideChange = currentRule.canChangeOverrideSettings === false;
    const hideOverrideChange = !landlordInfo?.isLandlord;

    return (
        <Layout>
            <AddRuleForm
                mode='edit'
                pageHeader={t('editSimpleRuleBreadcrumb', { ruleId: currentRule.ruleId })}
                formRef={formRef}
                parameterData={parameterData}
                listData={lData}
                mutation={mutation}
                formInitialValues={formInitialValues}
                selectedRowId={selectedRowId?.toString()}
                repoId={repoId}
                disabledInputFields={{ disableDefinitionChange, disableOverrideChange, hideOverrideChange }}
                conclusionFlagListData={conclusionFlagListData}
                disableDeleteRuleButton={!currentRule?.canDeleteRule?.canDelete}
                isNonTemplatedRule={currentRule?.nonTemplated}
                isRuleDeployed={currentRule?.canDeleteRule?.isRuleDeployed}
            />
        </Layout>
    );
};

export default UpdateSimpleRuleLayout;
